<script setup lang="ts">
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { computed, nextTick, useTemplateRef, watch } from "vue";
import {
  initialSceneMoveCollisionModel,
  updateSceneMoveCollisionFn,
  type SceneMoveCollisionCmd,
} from "./SceneMoveCollisionDialog.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";

const props = defineProps<{
  open: boolean;
  attemptedName: string;
  chapterName: string;
  error: Maybe<string>;
}>();

const emit = defineEmits<{
  close: [];
  retry: [name: string];
}>();

const input = useTemplateRef<InstanceType<typeof ZwInput>>("input");

async function runCmd(cmd: SceneMoveCollisionCmd): Promise<void> {
  switch (cmd.kind) {
    case "focusInput":
      await nextTick();
      input.value?.focus();
      input.value?.select();
      return;
    case "retry":
      emit("retry", cmd.name);
      return;
    default:
      assertNever(cmd);
  }
}

const { model, dispatch } = useTea(
  initialSceneMoveCollisionModel,
  updateSceneMoveCollisionFn,
  { runCmd: just(runCmd) },
);

const draft = computed({
  get: (): string => model.value.draft,
  set: (value: string): void => {
    void dispatch({ type: "DraftChanged", draft: value });
  },
});

watch(
  () => props.open,
  (open) => {
    if (open === false) return;
    void dispatch({ type: "Seed", attemptedName: props.attemptedName });
  },
);

function submit(): void {
  void dispatch({ type: "Submit" });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    submit();
  }
}
</script>

<template>
  <ZwDialog
    :open="open"
    title="Scene name already used"
    :description="
      just(
        `“${attemptedName}” already exists in “${chapterName}”. Choose a different name for the moved scene.`,
      )
    "
    size="md"
    @close="emit('close')"
  >
    <ZwInput
      :id="nothing"
      ref="input"
      v-model="draft"
      :label="just('Scene name')"
      aria-label="New scene name"
      @keydown="onKeydown"
    />
    <p v-if="isJust(error)" class="zw-dialog-error">{{ error.value }}</p>

    <template #footer>
      <ZwButton @click="emit('close')">Cancel</ZwButton>
      <ZwButton variant="primary" @click="submit">Move</ZwButton>
    </template>
  </ZwDialog>
</template>
